// A26 — Avtomatlashtirish. Module-local data layer (does NOT touch
// `lib/adapters.ts` / `i18n/*` — C-session HOT). Backend `/admin/automation/*`
// greenfield → fetchers fail-soft to empty; mutations POST when it ships.
// Registration = ./REGISTER.md (C applies routes/screenStatus/nav/i18n/perm).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(
  rows: T[],
  params: FetchParams,
  search?: (r: T) => string,
): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/** A26.01 — an automation workflow (trigger → actions). */
export interface WorkflowRow {
  id: number
  name: string
  trigger: string
  actions: number
  enabled: boolean
  last_run: string | null
  runs: number
}

/** A26.05 — one workflow execution. */
export interface ExecutionRow {
  id: number
  workflow: string
  status: 'ok' | 'error' | 'running'
  started_at: string
  duration_ms: number
  error: string | null
}

/** A26.07 — a scheduled (cron) task. */
export interface ScheduledTaskRow {
  id: number
  name: string
  schedule: string
  enabled: boolean
  next_run: string | null
  last_run: string | null
}

export function fetchWorkflows(params: FetchParams): Promise<FetchResult<WorkflowRow>> {
  return safeList<WorkflowRow>('/admin/automation/workflows', 'workflows', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.name} ${r.trigger}`),
  )
}

/** POST /admin/automation/workflows/:id/toggle {enabled} (A26.01). */
export function toggleWorkflow(id: number, enabled: boolean): Promise<{ enabled: boolean }> {
  return api.post<{ enabled: boolean }>(`/admin/automation/workflows/${id}/toggle`, { enabled })
}

/** POST /admin/automation/workflows/:id/run — run once now (A26.04 test). */
export function runWorkflow(id: number): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(`/admin/automation/workflows/${id}/run`, {})
}

/**
 * A26.02/A26.03 — one executable workflow step. Backend accepts only the safe,
 * internal types 'log' | 'set_flag'; a `set_flag` key MUST start with 'feature_'
 * (sandbox — a workflow can never rewrite a critical app setting).
 */
export interface AutoAction {
  type: 'log' | 'set_flag'
  message?: string
  key?: string
  value?: string
}

/** True if `a` is a valid action the backend will accept (mirrors validateAutoAction). */
export function isValidAction(a: AutoAction): boolean {
  if (a.type === 'log') return (a.message ?? '').trim().length > 0
  if (a.type === 'set_flag') return (a.key ?? '').startsWith('feature_')
  return false
}

/**
 * PUT /admin/automation/workflows/:id/actions — define (replace) a workflow's
 * executable action list (A26.02 builder / A26.03 conditions & actions editor).
 */
export function setWorkflowActions(
  id: number,
  actions: AutoAction[],
): Promise<{ ok: boolean; action_count: number }> {
  return api.put<{ ok: boolean; action_count: number }>(
    `/admin/automation/workflows/${id}/actions`,
    { actions },
  )
}

export function fetchExecutions(params: FetchParams): Promise<FetchResult<ExecutionRow>> {
  return safeList<ExecutionRow>('/admin/automation/executions', 'executions', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.workflow} ${r.status}`),
  )
}

export function fetchScheduled(params: FetchParams): Promise<FetchResult<ScheduledTaskRow>> {
  return safeList<ScheduledTaskRow>('/admin/automation/scheduled', 'tasks', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.name} ${r.schedule}`),
  )
}

/** POST /admin/automation/scheduled/:id/toggle {enabled} (A26.07). */
export function toggleScheduled(id: number, enabled: boolean): Promise<{ enabled: boolean }> {
  return api.post<{ enabled: boolean }>(`/admin/automation/scheduled/${id}/toggle`, { enabled })
}

/** A26.08 — an event trigger that fires workflows. */
export interface TriggerRow {
  id: number
  name: string
  event: string
  workflows: number
  enabled: boolean
}

/** A26.09 — an outgoing webhook fired by automation. */
export interface OutgoingWebhookRow {
  id: number
  url: string
  event: string
  active: boolean
  last_status: number | null
  failures: number
}

/** A26.06 — a failed workflow execution needing attention. */
export interface AutomationErrorRow {
  id: number
  workflow: string
  error: string
  occurred_at: string
  count: number
}

export function fetchTriggers(params: FetchParams): Promise<FetchResult<TriggerRow>> {
  return safeList<TriggerRow>('/admin/automation/triggers', 'triggers', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.name} ${r.event}`),
  )
}

/** POST /admin/automation/triggers/:id/toggle {enabled} (A26.08). */
export function toggleTrigger(id: number, enabled: boolean): Promise<{ enabled: boolean }> {
  return api.post<{ enabled: boolean }>(`/admin/automation/triggers/${id}/toggle`, { enabled })
}

export function fetchOutgoingWebhooks(
  params: FetchParams,
): Promise<FetchResult<OutgoingWebhookRow>> {
  return safeList<OutgoingWebhookRow>('/admin/automation/webhooks', 'webhooks', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.url} ${r.event}`),
  )
}

export function fetchErrors(params: FetchParams): Promise<FetchResult<AutomationErrorRow>> {
  return safeList<AutomationErrorRow>('/admin/automation/errors', 'errors', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.workflow} ${r.error}`),
  )
}

/**
 * A26.11 — a sandboxed automation script. Greenfield: `/admin/automation/scripts`
 * has not shipped yet, so the fetcher fail-softs to empty (dormant, like the other
 * lane-A automation screens) and lights up automatically when the endpoint lands.
 */
export interface ScriptRow {
  id: number
  name: string
  language: string
  status: 'active' | 'disabled' | 'error'
  updated_at: string | null
  last_run: string | null
}

export function fetchScripts(params: FetchParams): Promise<FetchResult<ScriptRow>> {
  return safeList<ScriptRow>('/admin/automation/scripts', 'scripts', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.language}`),
  )
}

/** A26.12 — an automation-related audit entry (from the real shared audit log). */
export interface AutomationAuditRow {
  id: number
  admin: string | null
  action: string
  target: string | null
  detail: string | null
  ip: string | null
  created_at: number
}

/**
 * A26.12 — automation audit. There is no dedicated automation-audit endpoint, so we
 * read the REAL shared audit log (`GET /admin/audit`) and keep only the automation_*
 * actions the automation handlers write (workflow toggle/run/actions, scheduled/
 * trigger toggle). Fail-soft to empty on any error.
 */
export async function fetchAutomationAudit(
  params: FetchParams,
): Promise<FetchResult<AutomationAuditRow>> {
  let rows: AutomationAuditRow[] = []
  try {
    const data = await api.get<{ items?: AutomationAuditRow[] }>('/admin/audit', {
      signal: params.signal,
      query: { limit: 200 },
    })
    rows = (data?.items ?? []).filter(
      (r) => typeof r.action === 'string' && r.action.startsWith('automation_'),
    )
  } catch {
    rows = []
  }
  return toResult(rows, params, (r) => `${r.action} ${r.admin ?? ''} ${r.detail ?? ''}`)
}

/** Module-local RU strings (i18n HOT → keyга ko'chiriladi, REGISTER.md). */
export const AUTO_STR = {
  workflows: 'Сценарии автоматизации',
  executions: 'Журнал выполнения',
  scheduled: 'Запланированные задачи',
  name: 'Название',
  trigger: 'Триггер',
  actions: 'Действий',
  enabled: 'Вкл.',
  lastRun: 'Последний запуск',
  runs: 'Запусков',
  workflow: 'Сценарий',
  status: 'Статус',
  startedAt: 'Начат',
  duration: 'Длительность, мс',
  error: 'Ошибка',
  schedule: 'Расписание',
  nextRun: 'Следующий запуск',
  enable: 'Включить',
  disable: 'Выключить',
  run: 'Запустить сейчас',
  toggled: 'Сценарий обновлён',
  started: 'Запуск инициирован',
  failed: 'Не удалось выполнить',
  // A26.08 triggers
  triggers: 'Триггеры',
  event: 'Событие',
  // A26.09 outgoing webhooks
  outgoing: 'Исходящие webhook',
  url: 'URL',
  active: 'Активен',
  lastStatus: 'Последний статус',
  failures: 'Ошибок',
  // A26.06 errors
  errors: 'Ошибки выполнения',
  occurredAt: 'Когда',
  count: 'Повторов',
  // A26.02 builder / A26.03 conditions & actions
  builder: 'Конструктор сценариев',
  conditions: 'Условия и действия',
  condition: 'Условие (триггер)',
  build: 'Конструктор',
  editActions: 'Действия сценария',
  actionType: 'Тип действия',
  actionLog: 'Лог',
  actionSetFlag: 'Установить флаг',
  message: 'Сообщение',
  flagKey: 'Ключ флага',
  flagValue: 'Значение',
  flagHint: 'Ключ должен начинаться с «feature_»',
  addAction: 'Добавить действие',
  removeAction: 'Удалить',
  save: 'Сохранить',
  saving: 'Сохранение…',
  cancel: 'Отмена',
  saved: 'Действия сохранены',
  noActions: 'Действий пока нет',
  redefineNote: 'Список действий сценария будет переопределён этим набором.',
  invalidActions: 'Проверьте действия: лог требует сообщение, флаг — ключ «feature_».',
  // A26.04 test mode
  testMode: 'Тестовый режим',
  runTest: 'Запустить тест',
  selectWorkflow: 'Сценарий для теста',
  testStarted: 'Тестовый запуск инициирован',
  testResults: 'Результаты выполнения',
  noWorkflows: 'Нет сценариев',
  // A26.10 integrations
  integrations: 'Интеграции',
  // A26.11 scripts (sandbox)
  scripts: 'Скрипты (песочница)',
  language: 'Язык',
  updatedAt: 'Обновлён',
  // A26.12 automation audit
  audit: 'Аудит автоматизации',
  time: 'Время',
  admin: 'Сотрудник',
  action: 'Действие',
  target: 'Объект',
  detail: 'Детали',
  ip: 'IP',
} as const
